import { ReactElement, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Chip,
  Tooltip,
  Avatar,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Button,
  Tabs,
  Tab,
  Card,
  CardBody,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@heroui/react";
import {
  Eye,
  FileXls,
  CurrencyEur,
  CalendarDot,
  Wrench,
  ArrowClockwise,
  StackSimple,
  User,
  Info,
  Plus,
  X,
  Tag,
  Trash,
  Camera,
  FileArrowDown,
  Envelope,
  DotsThree,
  GitDiff,
  SquaresFourIcon,
  FileTextIcon,
  CaretDown,
  CaretUp,
  Warning,
} from "@phosphor-icons/react";
import {
  ProjectStatus,
  ProjectDate,
  ProjectDateType,
  DocumentType,
  DocumentApprovalStatus,
  ProjectPart,
  DocumentLine,
} from "shared/types/renovation";
import { useUsers } from "shared/contexts/users.context";
import { UnitDTO } from "shared/types/unit";
import { formatCurrency } from "shared/utils/currency";
import DirectSearchComponent from "shared/components/searchBar/components/directSearchComponent";
import { ResourceType } from "shared/types/resource";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { toast } from "react-toastify";
import { Spinner as NextUISpinner } from "@heroui/react";
import { LabelSearch } from "shared/components/labelSearch/labelSearch";
import { UserSelection } from "shared/components/userSelection/userSelection";
import { useActiveUsers } from "shared/contexts/users.context";
import { RenovationLabel } from "shared/types/renovation";
import { InlineTitleEditor } from "./InlineTitleEditor";
import { useSettings } from "shared/contexts/settings.context";
import UniversalPickerModal from "shared/components/UniversalPickerModal/UniversalPickerModal";
import { sumDocumentLineAmounts } from "shared/utils/documentLineAmount";

// Tone classes per renovation label "type" so the chip colour matches the
// palette used in BudgetSplitDesigner / investment list.
const getLabelChipTone = (type?: string): string => {
  switch (type?.toLowerCase()) {
    case "esthetisch":
      return "border-pink-200 bg-pink-50 text-pink-700";
    case "energetisch":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "veiligheid":
      return "border-rose-200 bg-rose-50 text-rose-700";
    case "comfort":
      return "border-blue-200 bg-blue-50 text-blue-700";
    case "technisch":
      return "border-amber-200 bg-amber-50 text-amber-700";
    default:
      return "border-neutral-200 bg-neutral-50 text-neutral-700";
  }
};

interface ProjectCardProps {
  id: number;
  buildingId: string;
  status: ProjectStatus;
  units: UnitDTO[];
  title: string;
  budgetAmount: string;
  proposalAmount: string;
  invoiceAmount: string;
  projectDates: ProjectDate[];
  responsibleUserId?: string;
  actionIcon?: ReactElement;
  actionOnClick?: () => void;
  description?: string;
  parts?: ProjectPart[];
  onUpdate?: () => void;
  onScrollToDocuments?: (projectId?: number, projectPartId?: number) => void;
  isHighlighted?: boolean;
  projectYear?: number;
  onStatusChange?: (projectId: number, newStatus: ProjectStatus) => void;
  // Collapse functionality
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  // Per-card "Projectonderdelen" expand state
  isPartsExpanded?: boolean;
  onTogglePartsExpanded?: () => void;
  visibleSections?: {
    units: boolean;
    financial: boolean;
    parts: boolean;
    planning: boolean;
  };
}

const calculateSumOfBestOffers = (parts: ProjectPart[]): number => {
  const total = parts.reduce((sum, part) => {
    // Filter lines to only include those for offers
    const offerLines =
      part.documentLines?.filter((line) => {
        return line.document.type === DocumentType.OFFER;
      }) || [];

    // Group offer lines by their document id (or documentNumber if id isn't available)
    const offerSums = offerLines.reduce<Record<string, number>>(
      (group, line) => {
        const docId: any = line.document.id || line.document.documentNumber;
        // If this document hasn't been added yet, initialize its sum
        if (!group[docId]) {
          group[docId] = 0;
        }
        // Add the line amount to the document's total
        group[docId] += Number(line.totalInclVat);
        return group;
      },
      {},
    );

    // Determine the best offer for this part (lowest total among offers)
    const documentTotals = Object.values(offerSums);
    if (documentTotals.length > 0) {
      const bestOfferForPart = Math.min(...documentTotals);
      return sum + bestOfferForPart;
    }
    return sum;
  }, 0);

  return total;
};

const calculateApprovedOffersAmount = (parts: ProjectPart[]): number =>
  (parts ?? []).reduce(
    (sum, part) => sum + getApprovedOffersAmount(part),
    0,
  );

const getApprovedOffersAmount = (part: ProjectPart): number =>
  sumDocumentLineAmounts(
    part.documentLines?.filter(
      (line: DocumentLine) =>
        line.document.type === DocumentType.OFFER &&
        line.document.status === DocumentApprovalStatus.APPROVED,
    ),
  );

/**
 * Bestelbonnen op dit onderdeel — de stap tussen de goedgekeurde offerte en de
 * factuur: wat er effectief besteld is. Ongeacht goedkeuringsstatus, want een
 * bestelbon *is* de verbintenis en niet het voorstel.
 */
const getPurchaseOrderAmount = (part: ProjectPart): number =>
  sumDocumentLineAmounts(
    part.documentLines?.filter(
      (line: DocumentLine) => line.document.type === DocumentType.ORDER,
    ),
  );

// Budget now comes from investment item part assignments
const getPartBudgetAmount = (part: ProjectPart): number => {
  return (
    part.investmentAssignments?.reduce(
      (sum: number, assignment: any) => sum + Number(assignment.assignedAmount),
      0,
    ) || 0
  );
};

export const ProjectCard = ({
  id,
  buildingId,
  status,
  units,
  title,
  projectDates,
  responsibleUserId,
  actionOnClick,
  description,
  parts = [],
  onUpdate,
  onScrollToDocuments,
  isHighlighted = false,
  onStatusChange,
  isCollapsed = false,
  onToggleCollapse,
  isPartsExpanded,
  onTogglePartsExpanded,
  visibleSections = {
    units: true,
    financial: true,
    parts: true,
    planning: true,
  },
}: ProjectCardProps) => {
  const navigate = useNavigate();
  const { users } = useUsers();
  const { getRenovationCardSettings } = useSettings();
  const settings = getRenovationCardSettings();
  // Local fallback for uncontrolled "Projectonderdelen" expand state
  const [localPartsExpanded, setLocalPartsExpanded] = useState<boolean>(
    settings.partsExpanded ?? false,
  );
  const partsExpanded =
    isPartsExpanded !== undefined ? isPartsExpanded : localPartsExpanded;
  const togglePartsExpanded = () => {
    if (onTogglePartsExpanded) onTogglePartsExpanded();
    else setLocalPartsExpanded((prev) => !prev);
  };
  const [responsibleId, setResponsibleId] = useState<string | undefined>(
    responsibleUserId,
  );
  const responsibleUser = users.find((user) => user.id === responsibleId);
  const [activePartId, setActivePartId] = useState<number | null>(null);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const { session } = useSession();
  const [showAddPartModal, setShowAddPartModal] = useState(false);
  const [newPart, setNewPart] = useState({
    title: "",
    plannedStartDate: "",
    plannedEndDate: "",
    label: null as RenovationLabel | null,
  });
  const [isCreatingPart, setIsCreatingPart] = useState(false);
  // Kostenplaatsen ("voorziene units") for the new project part. Populated
  // when the create-part modal opens — building units form the chip palette,
  // the project's own provisional units are pre-selected so users get a
  // sensible default and only need to add/remove outliers.
  const [newPartAvailableUnits, setNewPartAvailableUnits] = useState<
    UnitDTO[]
  >([]);
  const [newPartSelectedUnitIds, setNewPartSelectedUnitIds] = useState<
    Set<string>
  >(new Set());
  const [currentStatus, setCurrentStatus] = useState(status);
  const allStatuses = Object.values(ProjectStatus); // e.g. ["Ontwerp fase", "Gepland", "In uitvoering", "Afgerond", "Volledig gefactureerd"]
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Cosmetic label mapping for project status
  const getStatusDisplayLabel = (status: ProjectStatus): string => {
    switch (status) {
      case ProjectStatus.DESIGN:
        return "Ontwerp";
      case ProjectStatus.PLANNED:
        return "Uitvoeringsdossier";
      case ProjectStatus.IN_PROGRESS:
        return "In uitvoering - Werf";
      case ProjectStatus.COMPLETED:
        return "Voorlopige oplevering";
      case ProjectStatus.PAID:
        return "Definitieve oplevering (Volledig gefactureerd)";
      default:
        return status;
    }
  };

  // Sync currentStatus when status prop changes from outside
  useEffect(() => {
    setCurrentStatus(status);
  }, [status]);

  // When the create-part modal opens, fetch the building's units (chip
  // palette) and the project's provisional units (pre-selected defaults).
  useEffect(() => {
    if (!showAddPartModal || !session) return;
    let cancelled = false;
    (async () => {
      try {
        const [unitsRes, projectRes] = await Promise.all([
          buildingId
            ? fetchWithAuth(
                `${API_URL}/units/building/${buildingId}`,
                { method: "GET" },
                session,
              )
            : Promise.resolve(null),
          fetchWithAuth(
            `${API_URL}/renovation/project/${id}`,
            { method: "GET" },
            session,
          ),
        ]);
        if (cancelled) return;

        if (unitsRes?.ok) {
          const data = await unitsRes.json();
          const list = Array.isArray(data) ? data : data.data || [];
          setNewPartAvailableUnits(list);
        }
        if (projectRes?.ok) {
          const project = await projectRes.json();
          const provisional: any[] = Array.isArray(project?.provisionalUnits)
            ? project.provisionalUnits
            : [];
          const provisionalIds = provisional
            .map((pu) => pu?.unit?.id)
            .filter((id: any): id is string => !!id);
          setNewPartSelectedUnitIds(new Set(provisionalIds));
        }
      } catch (err) {
        console.error(
          "[projectCard] Failed to load units for new part:",
          err,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showAddPartModal, session, buildingId, id]);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletionInfo, setDeletionInfo] = useState<{
    projectPartsCount: number;
    investmentAssignmentsCount: number;
    sharePointFoldersCount: number;
    analyticDistributionsCount: number;
    emailsCount: number;
    documentsCount: number;
    documents: Array<{
      id: number;
      type: string;
      documentNumber: string;
      supplier: string;
      totalAmount: number;
      partTitle: string;
    }>;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isResponsibleModalOpen, setIsResponsibleModalOpen] = useState(false);
  const { users: activeUsers } = useActiveUsers();

  // Pictures modal state
  const [showPicturesModal, setShowPicturesModal] = useState(false);

  // Renovation label picker state — drives the inline label chip on every
  // project-part card. `partLabelPicker` holds the part being edited so the
  // shared modal can resolve the current value + post the update.
  const [partLabelPicker, setPartLabelPicker] = useState<{
    partId: number;
    currentId: number | null;
  } | null>(null);
  const [renovationLabels, setRenovationLabels] = useState<RenovationLabel[]>(
    [],
  );

  // Fetch the label list once per card mount. The list is small (<50 items)
  // and shared across all parts in this project, so a single GET is fine.
  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuth(
          `${API_URL}/renovation/labels`,
          { method: "GET" },
          session,
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data)) setRenovationLabels(data);
      } catch (err) {
        console.error("Failed to load renovation labels:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session]);

  const handlePartLabelChange = async (val: string | null) => {
    if (!partLabelPicker) return;
    const labelId = val ? Number(val) : null;
    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/part/${partLabelPicker.partId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ labelId }),
        },
        session,
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || "Failed to update part label");
      }
      toast.success("Categorie bijgewerkt");
      onUpdate?.();
    } catch (error) {
      console.error("Error updating part label:", error);
      toast.error(
        error instanceof Error ? error.message : "Fout bij bijwerken categorie",
      );
    } finally {
      setPartLabelPicker(null);
    }
  };

  // RFP modal state
  const [showRFPModal, setShowRFPModal] = useState(false);
  const [selectedRFPPart, setSelectedRFPPart] = useState<ProjectPart | null>(
    null,
  );
  const [selectedRFPSupplier, setSelectedRFPSupplier] = useState<any>(null);

  // Dummy pictures data for UI/UX showcase
  const dummyPictures = {
    voorafgaand: [
      {
        id: 1,
        url: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop",
        caption: "Bestaande keuken voor renovatie",
      },
      {
        id: 2,
        url: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=300&fit=crop",
        caption: "Originele badkamer staat",
      },
      {
        id: 3,
        url: "https://images.unsplash.com/photo-1484154218962-a197022b5858?w=400&h=300&fit=crop",
        caption: "Woonkamer voor werkzaamheden",
      },
    ],
    werfopvolging: [
      {
        id: 4,
        url: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=300&fit=crop",
        caption: "Sloopwerkzaamheden in uitvoering",
      },
      {
        id: 5,
        url: "https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=400&h=300&fit=crop",
        caption: "Nieuwe leidingen installatie",
      },
      {
        id: 6,
        url: "https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=400&h=300&fit=crop",
        caption: "Tegelwerk in progress",
      },
      {
        id: 7,
        url: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=400&h=300&fit=crop",
        caption: "Elektriciteit en verlichting",
      },
    ],
    eindresultaat: [
      {
        id: 8,
        url: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop",
        caption: "Voltooide moderne keuken",
      },
      {
        id: 9,
        url: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=400&h=300&fit=crop",
        caption: "Gerenoveerde badkamer",
      },
      {
        id: 10,
        url: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=300&fit=crop",
        caption: "Afgewerkte woonruimte",
      },
    ],
  };

  const formatDate = (date?: Date | string) => {
    if (!date) return "-";
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleDateString("nl-BE");
  };

  const getLatestDate = (type: ProjectDateType) => {
    const typeMapping: { [key: string]: string } = {
      [ProjectDateType.EXPECTED_START]: "Verwachte startdatum",
      [ProjectDateType.EXPECTED_END]: "Verwachte einddatum",
      [ProjectDateType.ACTUAL_START]: "Effectieve startdatum",
      [ProjectDateType.ACTUAL_END]: "Effectieve einddatum",
    };

    const validDates = projectDates
      ?.filter((d) => d.type === typeMapping[type] && !d.isDeprecated)
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );

    return validDates?.[0]?.date;
  };

  const dates = {
    expectedStartDate: getLatestDate(ProjectDateType.EXPECTED_START),
    expectedEndDate: getLatestDate(ProjectDateType.EXPECTED_END),
    actualStartDate: getLatestDate(ProjectDateType.ACTUAL_START),
    actualEndDate: getLatestDate(ProjectDateType.ACTUAL_END),
  };

  const validateDates = () => {
    if (!dates.expectedStartDate || !dates.expectedEndDate) return true;
    return new Date(dates.expectedStartDate) <= new Date(dates.expectedEndDate);
  };

  // Budget now comes from investment item part assignments
  const calculateBudgetAmount = () => {
    const total = parts.reduce((sum, part) => {
      const partTotal =
        part.investmentAssignments?.reduce(
          (assignmentSum: number, assignment: any) => {
            return assignmentSum + Number(assignment.assignedAmount);
          },
          0,
        ) || 0;
      return sum + partTotal;
    }, 0);
    return total;
  };

  const calculateInvoiceAmount = () =>
    parts.reduce(
      (sum, part) =>
        sum +
        sumDocumentLineAmounts(
          part.documentLines?.filter(
            (line: any) => line.document.type === DocumentType.INVOICE,
          ),
        ),
      0,
    );

  /** Bestelbonnen over alle onderdelen van dit project. */
  const calculatePurchaseOrderAmount = () =>
    parts.reduce((sum, part) => sum + getPurchaseOrderAmount(part), 0);

  const calculatedBudgetAmount = calculateBudgetAmount();
  const calculatedBestOffers = calculateSumOfBestOffers(parts);

  // Aggregate unique labels from project parts (if any)

  const partsWithoutReceivedOffers = parts.filter((part) => {
    // Must have suppliers assigned
    const assignedSuppliers =
      part.supplierAssignments?.filter((sa: any) => sa.supplier) || [];
    if (assignedSuppliers.length === 0) return false; // Don't count parts without suppliers

    // Check if ANY assigned supplier has sent an offer (any status)
    const hasReceivedOffer = assignedSuppliers.some((sa: any) =>
      part.documentLines?.some(
        (line: DocumentLine) =>
          line.document.type === DocumentType.OFFER &&
          String(line.document.supplier?.id) === String(sa.supplier.id),
      ),
    );

    // Return true if NO offer received from any assigned supplier
    return !hasReceivedOffer;
  });

  const partsWithoutApprovedOffers = parts.filter((part) => {
    // Must have suppliers assigned
    const assignedSuppliers =
      part.supplierAssignments?.filter((sa: any) => sa.supplier) || [];
    if (assignedSuppliers.length === 0) return false; // Don't count parts without suppliers

    // First check if ANY assigned supplier has sent an offer (any status)
    const hasReceivedOffer = assignedSuppliers.some((sa: any) =>
      part.documentLines?.some(
        (line: DocumentLine) =>
          line.document.type === DocumentType.OFFER &&
          String(line.document.supplier?.id) === String(sa.supplier.id),
      ),
    );

    // If no offer received at all, don't count (that's in "zonder binnengekomen offerte")
    if (!hasReceivedOffer) return false;

    // Now check if ANY assigned supplier has an APPROVED offer
    const hasApprovedOffer = assignedSuppliers.some((sa: any) =>
      part.documentLines?.some(
        (line: DocumentLine) =>
          line.document.type === DocumentType.OFFER &&
          line.document.status === DocumentApprovalStatus.APPROVED &&
          String(line.document.supplier?.id) === String(sa.supplier.id),
      ),
    );

    // Return true if offer received BUT not approved
    return !hasApprovedOffer;
  });

  // Parts without suppliers (no RFP sent)
  const partsWithoutSuppliers = parts.filter((part) => {
    const assignedSuppliers =
      part.supplierAssignments?.filter((sa: any) => sa.supplier) || [];
    // Return true if no suppliers are assigned to this part
    return assignedSuppliers.length === 0;
  });

  // Single supplier search handler for all parts
  const handleSupplierSelect = async (supplierId: number) => {
    if (!activePartId) return;

    try {
      setSupplierLoading(true);

      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/part/${activePartId}/supplier/${supplierId}`,
        { method: "POST" },
        session,
      );

      if (!response.ok) {
        throw new Error("Failed to assign supplier");
      }

      setActivePartId(null);
      toast.success("Leverancier succesvol toegewezen");

      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Error assigning supplier:", error);
      toast.error("Failed to assign supplier");
    } finally {
      setSupplierLoading(false);
    }
  };

  const handleCreatePart = async () => {
    if (!newPart.title || !newPart.label) {
      toast.error("Titel en categorie zijn verplicht");
      return;
    }

    try {
      setIsCreatingPart(true);
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/${id}/part`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: newPart.title,
            label: newPart.label,
            plannedStartDate: newPart.plannedStartDate,
            plannedEndDate: newPart.plannedEndDate,
          }),
        },
        session,
      );

      if (!response.ok) {
        throw new Error("Failed to create project part");
      }

      // If the user picked kostenplaatsen, attach them as the part's
      // provisional units in a follow-up PUT. Failure here is non-fatal —
      // the part itself is already created and the user can edit units via
      // the Voorziene kostenplaatsen section.
      const newPartId = (await response.clone().json().catch(() => null))?.id;
      const selectedUnitIds = Array.from(newPartSelectedUnitIds);
      if (newPartId && selectedUnitIds.length > 0) {
        try {
          await fetchWithAuth(
            `${API_URL}/renovation/project/part/${newPartId}/provisional-units`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                units: selectedUnitIds.map((unitId) => ({
                  unitId,
                  mustBeEmpty: false,
                })),
              }),
            },
            session,
          );
        } catch (puErr) {
          console.error(
            "[projectCard] Failed to attach provisional units to new part:",
            puErr,
          );
          toast.warn(
            "Projectonderdeel aangemaakt, maar kostenplaatsen konden niet automatisch worden gekoppeld",
          );
        }
      }

      toast.success("Projectonderdeel succesvol toegevoegd");
      setShowAddPartModal(false);
      setNewPart({
        title: "",
        plannedStartDate: "",
        plannedEndDate: "",
        label: null,
      });
      setNewPartSelectedUnitIds(new Set());

      // Refresh the project data
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Error creating project part:", error);
      toast.error("Fout bij aanmaken projectonderdeel");
    } finally {
      setIsCreatingPart(false);
    }
  };

  const alignWithProjectDates = () => {
    // Use the same typeMapping as in getLatestDate
    const typeMapping = {
      [ProjectDateType.EXPECTED_START]: "Verwachte startdatum",
      [ProjectDateType.EXPECTED_END]: "Verwachte einddatum",
    };

    const startDate = projectDates
      ?.filter(
        (d) =>
          d.type === typeMapping[ProjectDateType.EXPECTED_START] &&
          !d.isDeprecated,
      )
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )[0]?.date;

    const endDate = projectDates
      ?.filter(
        (d) =>
          d.type === typeMapping[ProjectDateType.EXPECTED_END] &&
          !d.isDeprecated,
      )
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )[0]?.date;

    console.log("Found project dates:", { startDate, endDate });

    setNewPart((prev) => ({
      ...prev,
      plannedStartDate: startDate
        ? new Date(startDate).toISOString().split("T")[0]
        : "",
      plannedEndDate: endDate
        ? new Date(endDate).toISOString().split("T")[0]
        : "",
    }));
  };

  // Handler to update project status via the API
  const handleStatusUpdate = async (newStatus: string) => {
    if (newStatus === currentStatus) {
      return;
    }

    // Validate that newStatus is a valid ProjectStatus
    if (!Object.values(ProjectStatus).includes(newStatus as ProjectStatus)) {
      console.error("Invalid project status:", newStatus);
      return;
    }

    // Store old status for rollback
    const oldStatus = currentStatus;
    const newStatusTyped = newStatus as ProjectStatus;

    // OPTIMISTIC UPDATE: Update local UI immediately
    setCurrentStatus(newStatusTyped);

    // Notify parent to update its state (for kanban column movement)
    if (onStatusChange) {
      onStatusChange(id, newStatusTyped);
    }

    // Make API call in background
    try {
      setUpdatingStatus(true);
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/${id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: newStatus }),
        },
        session,
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to update project status");
      }

      toast.success(`Project verplaatst naar "${newStatus}"`);
      // Don't call onUpdate() - keep the optimistic update without re-rendering
    } catch (error: any) {
      console.error("Error updating project status:", error);
      toast.error(
        `Fout bij bijwerken project status: ${error.message || "Onbekende fout"}`,
      );

      // ROLLBACK: Revert local status
      setCurrentStatus(oldStatus);

      // Notify parent to rollback as well
      if (onStatusChange) {
        onStatusChange(id, oldStatus);
      }

      // Optionally refetch to ensure data consistency
      if (onUpdate) {
        onUpdate();
      }
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDeleteClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      // Fetch deletion info when opening modal
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/${id}/deletion-info`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${await session!.getToken()}`,
            "Content-Type": "application/json",
          },
        },
        session!,
      );

      if (response.ok) {
        const info = await response.json();
        console.log("Deletion info received:", info);
        setDeletionInfo(info);
        setShowDeleteModal(true);
        console.log("Modal should now be open:", true);
      } else {
        console.error("Response not ok:", response.status, response.statusText);
        toast.error("Fout bij ophalen project informatie");
      }
    } catch (error) {
      console.error("Error fetching deletion info:", error);
      toast.error("Fout bij ophalen project informatie");
    }
  };

  const handleConfirmDelete = async () => {
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/${id}/comprehensive`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${await session!.getToken()}`,
            "Content-Type": "application/json",
          },
        },
        session!,
      );

      if (response.ok) {
        toast.success("Project succesvol verwijderd");
        setShowDeleteModal(false);
        onUpdate?.(); // Refresh the projects list
      } else {
        toast.error("Fout bij verwijderen project");
      }
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Fout bij verwijderen project");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResponsibleSelect = async (user: any) => {
    try {
      const newId = user?.id;
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/${id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ responsibleUserId: newId }),
        },
        session,
      );
      if (!response.ok) throw new Error("Failed to update responsible user");
      setResponsibleId(newId);
      setIsResponsibleModalOpen(false);
      toast.success("Verantwoordelijke bijgewerkt");
      onUpdate?.();
    } catch (error) {
      console.error("Error updating responsible user:", error);
      toast.error("Fout bij bijwerken verantwoordelijke");
    }
  };

  const handlePartTitleUpdate = async (partId: number, newTitle: string) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/part/${partId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ title: newTitle }),
        },
        session,
      );
      if (!response.ok) throw new Error("Failed to update project part title");
      onUpdate?.(); // Refresh the project data
    } catch (error) {
      console.error("Error updating project part title:", error);
      throw error; // Re-throw to let InlineTitleEditor handle the error
    }
  };

  return (
    <div
      className={`group flex h-full cursor-pointer flex-col gap-2.5 overflow-hidden rounded-xl border border-neutral-200 p-3 shadow-sm transition-all duration-200 hover:cursor-grab hover:border-moss-primary/40 hover:shadow-md ${
        isHighlighted
          ? "bg-moss-primary/5 ring-moss-primary animate-pulse ring-2 ring-offset-2"
          : "bg-white"
      }`}
    >
      <header className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-neutral-primary text-sm font-medium">
              RENO-{id}
            </div>
            {/* Collapse/Expand Button */}
            {onToggleCollapse && (
              <Tooltip content={isCollapsed ? "Uitvouwen" : "Invouwen"}>
                <button
                  className="bg-moss-primary/10 hover:bg-moss-primary/20 text-moss-primary rounded-full p-1 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleCollapse();
                  }}
                >
                  {isCollapsed ? (
                    <CaretDown size={16} weight="bold" />
                  ) : (
                    <CaretUp size={16} weight="bold" />
                  )}
                </button>
              </Tooltip>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            {/* Assignee */}
            <Tooltip
              content={
                responsibleUser
                  ? `${responsibleUser.first_name} ${responsibleUser.last_name}`
                  : "Geen verantwoordelijke"
              }
            >
              <button
                className="border-neutral-primary/40 shrink-0 rounded-full border p-0.5 hover:bg-neutral-50"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsResponsibleModalOpen(true);
                }}
              >
                {responsibleUser && responsibleUser.image_url ? (
                  <Avatar
                    size="sm"
                    src={responsibleUser.image_url}
                    className="h-6 w-6"
                  />
                ) : (
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-neutral-200 text-xs text-neutral-600">
                    ?
                  </div>
                )}
              </button>
            </Tooltip>

            {/* Status Dropdown */}
            <Tooltip content="Verander fase">
              <div onClick={(e) => e.stopPropagation()}>
                <Dropdown>
                  <DropdownTrigger>
                    <Button
                      size="sm"
                      isIconOnly
                      className="bg-moss-primary/10 hover:bg-moss-primary/20 h-7 w-7 min-w-0 rounded-full border-none"
                      isDisabled={updatingStatus}
                    >
                      {updatingStatus ? (
                        <NextUISpinner size="sm" />
                      ) : (
                        <GitDiff
                          weight="fill"
                          size={18}
                          className="text-moss-primary"
                        />
                      )}
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu
                    aria-label="Project status"
                    onAction={(key) => handleStatusUpdate(key as string)}
                    selectedKeys={[currentStatus]}
                    selectionMode="single"
                  >
                    {allStatuses.map((statusOption) => (
                      <DropdownItem key={statusOption}>
                        {getStatusDisplayLabel(statusOption as ProjectStatus)}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              </div>
            </Tooltip>

            {/* Actions Dropdown */}
            <Tooltip content="Meer acties">
              <div onClick={(e) => e.stopPropagation()}>
                <Dropdown>
                  <DropdownTrigger>
                    <Button
                      size="sm"
                      isIconOnly
                      className="bg-moss-primary/10 hover:bg-moss-primary/20 h-7 w-7 min-w-0 rounded-full border-none"
                    >
                      <DotsThree
                        weight="bold"
                        size={18}
                        className="text-moss-primary"
                      />
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu aria-label="Project acties">
                    <DropdownItem
                      key="view"
                      startContent={<Eye weight="duotone" size={16} />}
                      onPress={actionOnClick}
                    >
                      Details bekijken
                    </DropdownItem>
                    <DropdownItem
                      key="documents"
                      startContent={<CurrencyEur weight="duotone" size={16} />}
                      onPress={() => onScrollToDocuments?.(id)}
                    >
                      Naar documenten
                    </DropdownItem>
                    <DropdownItem
                      key="pictures"
                      startContent={<Camera weight="duotone" size={16} />}
                      onPress={() => setShowPicturesModal(true)}
                    >
                      Project foto's
                    </DropdownItem>
                    <DropdownItem
                      key="rfp"
                      startContent={
                        <FileArrowDown weight="duotone" size={16} />
                      }
                      onPress={() => setShowRFPModal(true)}
                    >
                      Offerte aanvragen
                    </DropdownItem>
                    <DropdownItem
                      key="excel"
                      startContent={<FileXls weight="duotone" size={16} />}
                    >
                      Excel openen
                    </DropdownItem>
                    <DropdownItem
                      key="delete"
                      className="text-red-700"
                      color="danger"
                      startContent={<Trash weight="duotone" size={16} />}
                      onPress={(e) => handleDeleteClick(e as any)}
                    >
                      Project verwijderen
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>
            </Tooltip>
          </div>
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h3 className="text-neutral-primary min-w-0 flex-1 truncate text-base font-semibold">
            {title}
          </h3>
          {description && (
            <Tooltip content={description} className="max-w-lg">
              <Info
                size={16}
                className="text-neutral-secondary-mid hover:text-moss-primary shrink-0 cursor-help transition-colors"
                weight="duotone"
              />
            </Tooltip>
          )}
          {visibleSections.units &&
            currentStatus !== ProjectStatus.PAID &&
            !isCollapsed && (
              <Popover placement="bottom-start">
                <PopoverTrigger>
                  <div onClick={(e) => e.stopPropagation()}>
                    <Chip
                      size="sm"
                      className="shrink-0 cursor-pointer gap-1"
                      startContent={
                        <StackSimple
                          weight="duotone"
                          size={12}
                          className="shrink-0"
                        />
                      }
                      classNames={{
                        base: "h-5 bg-white px-1.5 border border-moss-primary text-moss-primary hover:bg-moss-primary/10 transition-colors",
                        content: "px-0 text-[10px] font-medium",
                      }}
                    >
                      {units.length} units
                    </Chip>
                  </div>
                </PopoverTrigger>
                <PopoverContent className="p-3">
                  <div className="space-y-2">
                    <h4 className="text-neutral-primary flex items-center gap-2 text-sm font-semibold">
                      <StackSimple
                        weight="duotone"
                        size={16}
                        className="text-moss-primary"
                      />
                      Units ({units.length})
                    </h4>
                    <div className="flex max-w-xs flex-wrap gap-2">
                      {units.map((unit) => (
                        <Chip
                          key={unit.id}
                          size="sm"
                          className="shrink-0 gap-1"
                          startContent={
                            <StackSimple
                              weight="duotone"
                              size={12}
                              className="shrink-0"
                            />
                          }
                          classNames={{
                            base: "h-fit bg-moss-primary/10 px-2 border border-moss-primary/30 text-moss-primary",
                            content: "px-0 font-medium text-xs",
                          }}
                        >
                          <span className="max-w-[120px] truncate">
                            {unit.id}
                          </span>
                        </Chip>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}
        </div>

        {/* Warning chips - Only show when enabled in settings and not in DESIGN or PAID status */}
        {settings.showWarnings &&
          currentStatus !== ProjectStatus.DESIGN &&
          currentStatus !== ProjectStatus.PAID && (
            <div className="flex flex-wrap gap-2">
              {partsWithoutSuppliers.length > 0 && (
                <Tooltip
                  content={`${partsWithoutSuppliers.length} projectonderdelen zonder toegewezen leveranciers (geen RFP verzonden)`}
                >
                  <Chip
                    size="sm"
                    className="shrink-0 cursor-help gap-1"
                    startContent={<Warning weight="fill" size={14} />}
                    classNames={{
                      base: "h-fit bg-orange-50 px-2 py-0.5 border border-orange-300 text-orange-700",
                      content: "px-0 font-medium text-xs",
                    }}
                  >
                    {partsWithoutSuppliers.length} zonder RFP
                  </Chip>
                </Tooltip>
              )}
              {partsWithoutReceivedOffers.length > 0 && (
                <Tooltip
                  content={`${partsWithoutReceivedOffers.length} projectonderdelen zonder binnengekomen offerte`}
                >
                  <Chip
                    size="sm"
                    className="shrink-0 cursor-help gap-1"
                    startContent={<Warning weight="fill" size={14} />}
                    classNames={{
                      base: "h-fit bg-yellow-50 px-2 py-0.5 border border-yellow-300 text-yellow-700",
                      content: "px-0 font-medium text-xs",
                    }}
                  >
                    {partsWithoutReceivedOffers.length} zonder offerte
                  </Chip>
                </Tooltip>
              )}
              {partsWithoutApprovedOffers.length > 0 && (
                <Tooltip
                  content={`${partsWithoutApprovedOffers.length} projectonderdelen zonder goedgekeurde offerte`}
                >
                  <Chip
                    size="sm"
                    className="shrink-0 cursor-help gap-1"
                    startContent={<Warning weight="fill" size={14} />}
                    classNames={{
                      base: "h-fit bg-brick-primary/10 px-2 py-0.5 border border-brick-primary text-brick-primary",
                      content: "px-0 font-medium text-xs",
                    }}
                  >
                    {partsWithoutApprovedOffers.length} zonder goedkeuring
                  </Chip>
                </Tooltip>
              )}
            </div>
          )}
      </header>

      {/* Show sections only when not collapsed */}
      {!isCollapsed && (
        <>
          {/* Hide financial overview when project is fully invoiced */}
          {visibleSections.financial &&
            currentStatus !== ProjectStatus.PAID && (
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                  <CurrencyEur
                    weight="duotone"
                    size={16}
                    className="text-moss-primary shrink-0"
                  />
                  <h4 className="text-neutral-primary text-sm font-medium whitespace-nowrap">
                    Financieel overzicht
                  </h4>
                  {currentStatus !== ProjectStatus.DESIGN &&
                    calculatedBestOffers > calculatedBudgetAmount &&
                    calculatedBestOffers > 0 && (
                      <Tooltip content="Som van de beste offertes overschrijdt het budget">
                        <Chip
                          size="sm"
                          className="shrink-0 animate-pulse gap-1"
                          classNames={{
                            base: "h-fit bg-brick-primary/10 px-2 border border-brick-primary text-brick-primary",
                            content: "px-0 font-medium text-xs",
                          }}
                        >
                          Budget overschreden
                        </Chip>
                      </Tooltip>
                    )}
                  <div className="ml-2 flex-1 border-b border-neutral-300"></div>
                </div>
                <ul className="text-neutral-secondary-mid flex flex-col gap-0.5 text-xs [&>li]:flex [&>li]:items-center [&>li]:justify-between">
                  <li>
                    <span className="text-neutral-600">Budget:</span>
                    <span className="text-neutral-primary font-medium">
                      {formatCurrency(calculatedBudgetAmount)}
                    </span>
                  </li>
                  <Tooltip content="Een offerte is goedgekeurd wanneer deze in de documententabel status 'goedgekeurd' heeft. Klik om gefilterde lijst te bekijken.">
                    <li>
                      <span className="text-neutral-600">
                        Goedgekeurde offertes:
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/renovation/documents?projectId=${id}&documentType=Offerte&documentStatus=Goedgekeurd`);
                        }}
                        className="text-moss-primary font-medium hover:underline transition-colors"
                        title="Bekijk goedgekeurde offertes"
                      >
                        {formatCurrency(calculateApprovedOffersAmount(parts))}
                      </button>
                    </li>
                  </Tooltip>
                  {/* Bestelbon staat tussen offerte en factuur: wat er besteld
                      is. Enkel tonen wanneer er ook bestelbonnen zijn — anders
                      staat er op elk project een regel "€ 0". */}
                  {calculatePurchaseOrderAmount() !== 0 && (
                    <li>
                      <span className="text-neutral-600">Bestelbonnen:</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/renovation/documents?projectId=${id}&documentType=Bestelbon`);
                        }}
                        className="text-moss-primary font-medium hover:underline transition-colors"
                        title="Bekijk bestelbonnen"
                      >
                        {formatCurrency(calculatePurchaseOrderAmount())}
                      </button>
                    </li>
                  )}
                  <li>
                    <span className="text-neutral-600">Facturen:</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/renovation/documents?projectId=${id}&documentType=Factuur`);
                      }}
                      className="text-moss-primary font-medium hover:underline transition-colors"
                      title="Bekijk facturen"
                    >
                      {formatCurrency(calculateInvoiceAmount())}
                    </button>
                  </li>
                </ul>
              </div>
            )}

          {/* Hide project parts when project is fully invoiced */}
          {visibleSections.parts && currentStatus !== ProjectStatus.PAID && (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePartsExpanded();
                  }}
                  className="text-moss-primary hover:bg-moss-primary/10 flex h-5 w-5 shrink-0 items-center justify-center rounded-md transition-colors"
                  title={
                    partsExpanded
                      ? "Projectonderdelen invouwen"
                      : "Projectonderdelen uitvouwen"
                  }
                  aria-expanded={partsExpanded}
                >
                  {partsExpanded ? (
                    <CaretUp size={14} weight="bold" />
                  ) : (
                    <CaretDown size={14} weight="bold" />
                  )}
                </button>
                <SquaresFourIcon
                  weight="duotone"
                  size={16}
                  className="text-moss-primary shrink-0"
                />
                <h4 className="text-neutral-primary text-sm font-medium whitespace-nowrap">
                  Projectonderdelen
                </h4>
                <span className="shrink-0 rounded-full bg-neutral-200 px-2 py-0.5 text-xs text-neutral-500">
                  {parts.length}
                </span>
                <div className="ml-2 flex-1 border-b border-neutral-300"></div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAddPartModal(true);
                  }}
                  className="text-moss-primary hover:bg-moss-primary/10 flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-all hover:scale-110"
                  title="Onderdeel toevoegen"
                >
                  <Plus size={16} weight="bold" />
                </button>
              </div>
              {partsExpanded && (
              <div className="divide-y divide-neutral-200 overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50/30">
                {parts.map((part) => {
                  const invoicedAmount =
                    part.documentLines
                      ?.filter(
                        (line: any) =>
                          line.document.type === DocumentType.INVOICE,
                      )
                      ?.reduce(
                        (sum: number, line: any) =>
                          sum + Number(line.totalInclVat),
                        0,
                      ) || 0;

                  // Simplified view for DESIGN phase
                  if (currentStatus === ProjectStatus.DESIGN) {
                    return (
                      <div key={part.id} className="px-2.5 py-1.5">
                        <div className="flex min-w-0 items-baseline gap-2">
                          <span className="shrink-0 text-[10px] font-normal text-neutral-400">
                            RENO-{id}-O{part.id}
                          </span>
                          <h5 className="text-neutral-primary min-w-0 truncate text-sm font-medium">
                            {part.title}
                          </h5>
                        </div>
                      </div>
                    );
                  }

                  // Full view for other phases
                  return (
                    <Popover
                      key={part.id}
                      placement="right"
                      showArrow
                      offset={10}
                      shouldCloseOnInteractOutside={(element) => {
                        // Don't close if clicking on another popover or modal
                        const isPopover = element.closest(
                          '[data-slot="popover"]',
                        );
                        const isModal = element.closest('[role="dialog"]');
                        return !isPopover && !isModal;
                      }}
                    >
                      <PopoverTrigger>
                        <div
                          className="group cursor-pointer px-2.5 py-1.5 transition-colors hover:bg-white"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex min-w-0 items-start justify-between gap-2">
                            <div className="flex min-w-0 flex-1 flex-col gap-1">
                              {/* ID + Title on one row */}
                              <div className="flex min-w-0 items-baseline gap-2">
                                <span className="shrink-0 text-[10px] font-normal text-neutral-400">
                                  RENO-{id}-O{part.id}
                                </span>
                                <h5 className="text-neutral-primary group-hover:text-moss-primary min-w-0 truncate text-sm font-medium transition-colors">
                                  {part.title}
                                </h5>
                              </div>

                              {/* Label + Suppliers combined in a single wrap row */}
                              <div className="flex flex-wrap items-center gap-1.5">
                                {part.label ? (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPartLabelPicker({
                                        partId: part.id,
                                        currentId: part.label?.id ?? null,
                                      });
                                    }}
                                    className={`inline-flex h-5 items-center gap-1 rounded-full border px-2 text-[10px] font-medium transition-colors hover:shadow-sm ${getLabelChipTone(
                                      (part.label as any)?.type,
                                    )}`}
                                    title="Klik om categorie te wijzigen"
                                  >
                                    <Tag size={10} weight="fill" />
                                    <span className="max-w-[140px] truncate">
                                      {part.label.name}
                                    </span>
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPartLabelPicker({
                                        partId: part.id,
                                        currentId: null,
                                      });
                                    }}
                                    className="hover:bg-moss-primary/10 hover:text-moss-primary hover:border-moss-primary/40 inline-flex h-5 items-center gap-1 rounded-full border border-dashed border-neutral-300 bg-neutral-50 px-2 text-[10px] font-medium text-neutral-500 transition-colors"
                                    title="Categorie toevoegen"
                                  >
                                    <Tag size={10} />
                                    <span>Categorie</span>
                                  </button>
                                )}

                                {part.supplierAssignments?.filter(
                                  (sa: any) => sa.supplier,
                                ).length > 0 ? (
                                  <>
                                    {part.supplierAssignments
                                      ?.filter((sa: any) => sa.supplier)
                                      .map((sa: any) => {
                                        const hasApprovedOffer =
                                          part.documentLines?.some(
                                            (line: DocumentLine) =>
                                              line.document.type ===
                                                DocumentType.OFFER &&
                                              line.document.status ===
                                                DocumentApprovalStatus.APPROVED &&
                                              line.document.supplier?.id ===
                                                sa.supplier.id,
                                          );

                                        const hasInvoices =
                                          part.documentLines?.some(
                                            (line: DocumentLine) =>
                                              line.document.type ===
                                                DocumentType.INVOICE &&
                                              line.document.supplier?.id ===
                                                sa.supplier.id,
                                          );

                                        return (
                                          <a
                                            key={sa.id}
                                            href={sa.supplierFolderUrl}
                                            target="_blank"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <Chip
                                              size="sm"
                                              className="cursor-pointer gap-1 transition-transform hover:scale-105"
                                              startContent={
                                                <User
                                                  weight="duotone"
                                                  size={10}
                                                />
                                              }
                                              classNames={{
                                                base: hasInvoices
                                                  ? "h-fit bg-status-good px-2 py-0.5 border border-status-good text-white"
                                                  : hasApprovedOffer
                                                    ? "h-fit bg-moss-primary px-2 py-0.5 border border-moss-primary text-white"
                                                    : "h-fit bg-white px-2 py-0.5 border border-moss-primary/40 text-moss-primary hover:border-moss-primary",
                                                content:
                                                  "px-0 text-[10px] font-medium",
                                              }}
                                            >
                                              <span className="max-w-[120px] truncate">
                                                {sa.supplier.name}
                                              </span>
                                            </Chip>
                                          </a>
                                        );
                                      })}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActivePartId(part.id);
                                      }}
                                      className="bg-moss-primary/10 hover:bg-moss-primary/20 text-moss-primary flex h-5 items-center gap-0.5 rounded-full px-1.5 text-[10px] font-medium transition-colors"
                                      title="Leverancier toevoegen"
                                    >
                                      <Plus size={10} />
                                      <span>Toevoegen</span>
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActivePartId(part.id);
                                    }}
                                    className="hover:bg-moss-primary/10 hover:text-moss-primary flex h-5 items-center gap-0.5 rounded-full border border-dashed border-neutral-300 bg-neutral-100 px-1.5 text-[10px] font-medium text-neutral-500 transition-colors"
                                    title="Eerste leverancier toevoegen"
                                  >
                                    <Plus size={10} />
                                    <span>Leverancier toevoegen</span>
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Document button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onScrollToDocuments?.(id, part.id);
                              }}
                              className="hover:text-moss-primary mt-1 shrink-0 text-neutral-400 transition-colors"
                              title="Ga naar documenten"
                            >
                              <FileTextIcon size={16} weight="duotone" />
                            </button>
                          </div>
                        </div>
                      </PopoverTrigger>
                      <PopoverContent className="w-96 p-0!">
                        <div className="w-full space-y-0 overflow-hidden">
                          {/* Header Section with gradient */}
                          <div className="bg-moss-primary/5 border-moss-primary/20 rounded-tl-xl rounded-tr-xl border-b p-4">
                            <div className="flex items-start gap-3">
                              <div className="bg-moss-primary/20 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
                                <Wrench
                                  size={20}
                                  weight="duotone"
                                  className="text-moss-primary"
                                />
                              </div>
                              <div
                                className="min-w-0 flex-1"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                <InlineTitleEditor
                                  title={part.title}
                                  onSave={(newTitle) =>
                                    handlePartTitleUpdate(part.id, newTitle)
                                  }
                                  placeholder="Voer onderdeel titel in..."
                                  maxLength={60}
                                  showCharacterLimit={true}
                                  isProjectPart={true}
                                />
                                <p className="mt-1 text-xs text-neutral-500">
                                  RENO-{id}-O{part.id}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="space-y-0 p-4">
                            {/* Label */}
                            {part.label && (
                              <div className="pb-4">
                                <div className="mb-3 flex items-center gap-2">
                                  <Tag
                                    weight="duotone"
                                    size={16}
                                    className="text-moss-primary shrink-0"
                                  />
                                  <h4 className="text-neutral-primary text-sm font-medium whitespace-nowrap">
                                    Labels
                                  </h4>
                                  <div className="ml-3 flex-1 border-b border-neutral-300"></div>
                                </div>
                                <Chip
                                  size="sm"
                                  className="text-moss-primary border-moss-primary border bg-white"
                                >
                                  {part.label.name} (
                                  {part.label.type.charAt(0).toUpperCase() +
                                    part.label.type.slice(1)}
                                  )
                                </Chip>
                              </div>
                            )}

                            {/* Financial Overview */}
                            <div className={`pb-4 ${part.label ? "pt-4" : ""}`}>
                              <div className="mb-3 flex items-center gap-2">
                                <CurrencyEur
                                  weight="duotone"
                                  size={16}
                                  className="text-moss-primary shrink-0"
                                />
                                <h4 className="text-neutral-primary text-sm font-medium whitespace-nowrap">
                                  Financieel overzicht
                                </h4>
                                <div className="ml-3 flex-1 border-b border-neutral-300"></div>
                              </div>
                              <div className="space-y-2.5">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-neutral-600">
                                    Budget:
                                  </span>
                                  <span className="text-neutral-primary text-sm font-semibold">
                                    {formatCurrency(getPartBudgetAmount(part))}
                                  </span>
                                </div>

                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-neutral-600">
                                    Goedgekeurde offertes:
                                  </span>
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/renovation/documents?projectId=${id}&projectPartId=${part.id}&documentType=Offerte&documentStatus=Goedgekeurd`);
                                      }}
                                      className={`text-sm font-semibold hover:underline transition-colors ${
                                        getApprovedOffersAmount(part) >
                                        getPartBudgetAmount(part)
                                          ? "text-brick-primary"
                                          : "text-moss-primary"
                                      }`}
                                      title="Bekijk goedgekeurde offertes voor dit onderdeel"
                                    >
                                      {formatCurrency(
                                        getApprovedOffersAmount(part),
                                      )}
                                    </button>
                                    {getApprovedOffersAmount(part) >
                                      getPartBudgetAmount(part) && (
                                      <Tooltip content="Overschrijdt budget">
                                        <Info
                                          size={14}
                                          className="text-brick-primary"
                                          weight="fill"
                                        />
                                      </Tooltip>
                                    )}
                                  </div>
                                </div>

                                {getPurchaseOrderAmount(part) !== 0 && (
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm text-neutral-600">
                                      Bestelbon:
                                    </span>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/renovation/documents?projectId=${id}&projectPartId=${part.id}&documentType=Bestelbon`);
                                      }}
                                      className="text-moss-primary text-sm font-semibold transition-colors hover:underline"
                                      title="Bekijk bestelbonnen voor dit onderdeel"
                                    >
                                      {formatCurrency(getPurchaseOrderAmount(part))}
                                    </button>
                                  </div>
                                )}

                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-neutral-600">
                                    Gefactureerd:
                                  </span>
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/renovation/documents?projectId=${id}&projectPartId=${part.id}&documentType=Factuur`);
                                      }}
                                      className={`text-sm font-semibold hover:underline transition-colors ${
                                        invoicedAmount >
                                        getApprovedOffersAmount(part)
                                          ? "text-brick-primary"
                                          : "text-moss-primary"
                                      }`}
                                      title="Bekijk facturen voor dit onderdeel"
                                    >
                                      {formatCurrency(invoicedAmount)}
                                    </button>
                                    {invoicedAmount >
                                      getApprovedOffersAmount(part) && (
                                      <Tooltip content="Overschrijdt offertes">
                                        <Info
                                          size={14}
                                          className="text-brick-primary"
                                          weight="fill"
                                        />
                                      </Tooltip>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Suppliers */}
                            <div className="pt-4">
                              <div className="mb-3 flex items-center gap-2">
                                <User
                                  weight="duotone"
                                  size={16}
                                  className="text-moss-primary shrink-0"
                                />
                                <h4 className="text-neutral-primary text-sm font-medium whitespace-nowrap">
                                  Leveranciers
                                </h4>
                                <span className="bg-moss-primary/10 text-moss-primary shrink-0 rounded-full px-2 py-0.5 text-xs">
                                  {part.supplierAssignments?.filter(
                                    (sa: any) => sa.supplier,
                                  ).length || 0}
                                </span>
                                <div className="ml-3 flex-1 border-b border-neutral-300"></div>
                              </div>
                              {part.supplierAssignments?.filter(
                                (sa: any) => sa.supplier,
                              ).length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {part.supplierAssignments
                                    ?.filter((sa: any) => sa.supplier)
                                    .map((sa: any) => {
                                      const hasApprovedOffer =
                                        part.documentLines?.some(
                                          (line: DocumentLine) =>
                                            line.document.type ===
                                              DocumentType.OFFER &&
                                            line.document.status ===
                                              DocumentApprovalStatus.APPROVED &&
                                            line.document.supplier?.id ===
                                              sa.supplier.id,
                                        );

                                      const hasInvoices =
                                        part.documentLines?.some(
                                          (line: DocumentLine) =>
                                            line.document.type ===
                                              DocumentType.INVOICE &&
                                            line.document.supplier?.id ===
                                              sa.supplier.id,
                                        );

                                      return (
                                        <a
                                          key={sa.id}
                                          href={sa.supplierFolderUrl}
                                          target="_blank"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <Chip
                                            size="sm"
                                            className="cursor-pointer gap-1 transition-transform hover:scale-105"
                                            startContent={
                                              <User
                                                weight="duotone"
                                                size={12}
                                              />
                                            }
                                            classNames={{
                                              base: hasInvoices
                                                ? "h-fit bg-status-good px-2.5 py-1 border border-status-good text-white"
                                                : hasApprovedOffer
                                                  ? "h-fit bg-moss-primary px-2.5 py-1 border border-moss-primary text-white"
                                                  : "h-fit bg-white px-2.5 py-1 border border-moss-primary/40 text-moss-primary hover:border-moss-primary",
                                              content:
                                                "px-0 text-xs font-medium",
                                            }}
                                          >
                                            {sa.supplier.name}
                                          </Chip>
                                        </a>
                                      );
                                    })}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActivePartId(part.id);
                                    }}
                                    className="bg-moss-primary/10 hover:bg-moss-primary/20 text-moss-primary flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors"
                                  >
                                    <Plus size={12} />
                                    Toevoegen
                                  </button>
                                </div>
                              ) : (
                                <div className="rounded-lg border border-dashed border-neutral-200 bg-neutral-50 p-4 text-center">
                                  <p className="mb-2 text-sm text-neutral-500">
                                    Nog geen leveranciers
                                  </p>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActivePartId(part.id);
                                    }}
                                    className="bg-moss-primary hover:bg-moss-primary/90 mx-auto flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-white transition-colors"
                                  >
                                    <Plus size={14} />
                                    Leverancier toevoegen
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  );
                })}
              </div>
              )}
            </div>
          )}

          {/* Hide planning when project is fully invoiced */}
          {visibleSections.planning && currentStatus !== ProjectStatus.PAID && (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <CalendarDot
                  weight="duotone"
                  size={16}
                  className="text-moss-primary shrink-0"
                />
                <h4 className="text-neutral-primary text-sm font-medium whitespace-nowrap">
                  Planning
                </h4>
                {!validateDates() && (
                  <Tooltip content="Einddatum ligt voor startdatum">
                    <Chip
                      size="sm"
                      className="shrink-0 animate-pulse gap-1"
                      classNames={{
                        base: "h-fit bg-brick-primary/10 px-2 border border-brick-primary text-brick-primary",
                        content: "px-0 font-medium text-xs",
                      }}
                    >
                      Ongeldige planning
                    </Chip>
                  </Tooltip>
                )}
                <div className="ml-2 flex-1 border-b border-neutral-300"></div>
              </div>
              <ul className="text-neutral-secondary-mid flex flex-col gap-0.5 text-xs [&>li]:flex [&>li]:items-center [&>li]:justify-between">
                <li>
                  <span className="text-neutral-600">Start datum:</span>
                  <span
                    className={`font-medium ${!validateDates() ? "text-brick-primary" : "text-neutral-primary"}`}
                  >
                    {formatDate(dates.expectedStartDate)}
                  </span>
                </li>
                <li>
                  <span className="text-neutral-600">Eind datum:</span>
                  <span
                    className={`font-medium ${!validateDates() ? "text-brick-primary" : "text-neutral-primary"}`}
                  >
                    {formatDate(dates.expectedEndDate)}
                  </span>
                </li>
                {dates.actualStartDate && (
                  <li>
                    <span className="text-neutral-600">Effectieve start:</span>
                    <span className="text-neutral-primary font-medium">
                      {formatDate(dates.actualStartDate)}
                    </span>
                  </li>
                )}
                {dates.actualEndDate && (
                  <li>
                    <span className="text-neutral-600">Effectieve eind:</span>
                    <span className="text-neutral-primary font-medium">
                      {formatDate(dates.actualEndDate)}
                    </span>
                  </li>
                )}
                {dates.expectedEndDate && dates.expectedStartDate && (
                  <li>
                    <span className="text-neutral-600">Doorlooptijd:</span>
                    <span className="text-neutral-primary font-medium">
                      {Math.ceil(
                        (new Date(dates.expectedEndDate).getTime() -
                          new Date(dates.expectedStartDate).getTime()) /
                          (1000 * 60 * 60 * 24),
                      )}{" "}
                      dagen
                    </span>
                  </li>
                )}
              </ul>
            </div>
          )}
        </>
      )}

      {activePartId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            className="mx-4 w-full max-w-md rounded-lg bg-white p-6"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Leverancier toevoegen</h3>
                <button
                  onClick={() => setActivePartId(null)}
                  className="text-neutral-500 hover:text-neutral-700"
                >
                  <X size={20} />
                </button>
              </div>
              <DirectSearchComponent
                resourceFilter={[ResourceType.COMPANY]}
                onSelect={(resource) =>
                  handleSupplierSelect(resource.resourceId)
                }
                label="Zoek een leverancier..."
              />
              {supplierLoading && (
                <div className="flex items-center gap-2 text-sm text-neutral-500">
                  <NextUISpinner size="sm" />
                  <span>Leverancier toevoegen...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Part Modal */}
      <Modal
        isOpen={showAddPartModal}
        onClose={() => setShowAddPartModal(false)}
        size="2xl"
      >
        <ModalContent
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <ModalHeader className="flex flex-col gap-1">
            <h3 className="text-lg font-medium">Nieuw projectonderdeel</h3>
            <p className="text-sm text-neutral-500">
              Voeg een nieuw onderdeel toe aan dit project
            </p>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <Input
                label="Titel"
                value={newPart.title}
                onChange={(e) =>
                  setNewPart((prev) => ({ ...prev, title: e.target.value }))
                }
                isRequired
              />

              <div className="space-y-2">
                <label className="text-sm font-medium">Categorie</label>
                <LabelSearch
                  value={newPart.label}
                  onChange={(label) =>
                    setNewPart((prev) => ({ ...prev, label }))
                  }
                  placeholder="Selecteer een categorie"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="relative">
                  <Input
                    type="date"
                    label="Startdatum"
                    placeholder="dd/mm/yyyy"
                    value={newPart.plannedStartDate}
                    onChange={(e) =>
                      setNewPart((prev) => ({
                        ...prev,
                        plannedStartDate: e.target.value,
                      }))
                    }
                    classNames={{
                      input: "text-small",
                    }}
                  />
                </div>
                <div className="relative">
                  <Input
                    type="date"
                    label="Einddatum"
                    placeholder="dd/mm/yyyy"
                    value={newPart.plannedEndDate}
                    onChange={(e) =>
                      setNewPart((prev) => ({
                        ...prev,
                        plannedEndDate: e.target.value,
                      }))
                    }
                    classNames={{
                      input: "text-small",
                    }}
                  />
                </div>
              </div>

              <Button
                color="primary"
                variant="bordered"
                startContent={<ArrowClockwise size={16} />}
                onClick={alignWithProjectDates}
                className="bg-brick-primary hover:bg-brick-primary/90 text-white"
              >
                Aligneer met project planning
              </Button>

              {/* Kostenplaatsen — pre-selected from the project's provisional
                  units so a fresh part starts with the same scope as the
                  project. Click chips to add/remove before creating. */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  Kostenplaatsen
                  <span className="text-neutral-secondary-mid ml-1.5 text-[11px] font-normal">
                    standaard de verwachte kostenplaatsen op het project —
                    verwijder of voeg toe
                  </span>
                </label>
                {newPartAvailableUnits.length === 0 ? (
                  <div className="text-neutral-secondary-mid text-xs italic py-2">
                    Geen units gevonden voor dit gebouw.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1.5 max-h-[140px] overflow-y-auto rounded-md border border-neutral-200 bg-neutral-50 p-2">
                    {newPartAvailableUnits.map((u) => {
                      if (!u.id) return null;
                      const active = newPartSelectedUnitIds.has(u.id);
                      return (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => {
                            setNewPartSelectedUnitIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(u.id!)) next.delete(u.id!);
                              else next.add(u.id!);
                              return next;
                            });
                          }}
                          className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium transition-colors ${
                            active
                              ? "bg-moss-primary/10 border-moss-primary/40 text-moss-primary"
                              : "bg-white border-neutral-200 text-neutral-500 hover:border-neutral-300"
                          }`}
                        >
                          {active ? (
                            <Plus
                              size={10}
                              weight="bold"
                              className="rotate-45"
                            />
                          ) : (
                            <Plus size={10} weight="bold" />
                          )}
                          {u.id}
                        </button>
                      );
                    })}
                  </div>
                )}
                {newPartSelectedUnitIds.size > 0 && (
                  <div className="text-neutral-secondary-mid text-[10px]">
                    {newPartSelectedUnitIds.size} unit
                    {newPartSelectedUnitIds.size === 1 ? "" : "s"} geselecteerd
                  </div>
                )}
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setShowAddPartModal(false)}>
              Annuleren
            </Button>
            <Button
              color="primary"
              onPress={handleCreatePart}
              isLoading={isCreatingPart}
            >
              Toevoegen
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        size="lg"
      >
        <ModalContent
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <ModalHeader className="flex flex-col gap-1">
            <h3 className="text-brick-primary text-lg font-medium">
              Project verwijderen
            </h3>
            <p className="text-sm text-neutral-500">
              Deze actie kan niet ongedaan worden gemaakt
            </p>
          </ModalHeader>
          <ModalBody>
            {deletionInfo ? (
              <div className="space-y-4">
                <p className="text-sm">
                  Ben je zeker dat je dit project <strong>"{title}"</strong> wil
                  verwijderen?
                </p>

                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <h4 className="mb-3 text-sm font-medium text-amber-800">
                    Je verwijdert hiermee:
                  </h4>
                  <ul className="space-y-1 text-sm text-amber-700">
                    <li>
                      • {deletionInfo.projectPartsCount} projectonderdelen
                    </li>
                    <li>
                      • {deletionInfo.investmentAssignmentsCount} investeringslijn
                      toewijzingen
                    </li>
                    <li>
                      • {deletionInfo.sharePointFoldersCount} SharePoint mappen
                    </li>
                    <li>
                      • {deletionInfo.analyticDistributionsCount} analytische
                      verdelingen
                    </li>
                    <li>• {deletionInfo.emailsCount} emails</li>
                    <li>
                      • {deletionInfo.documentsCount} documenten
                      (facturen/offertes)
                    </li>
                  </ul>
                </div>

                {deletionInfo.documents.length > 0 && (
                  <div className="bg-brick-50 border-brick-200 rounded-lg border p-4">
                    <h4 className="text-brick-800 mb-3 text-sm font-medium">
                      Documenten die worden verwijderd:
                    </h4>
                    <div className="max-h-32 space-y-2 overflow-y-auto">
                      {deletionInfo.documents.map((doc) => (
                        <div
                          key={doc.id}
                          className="border-brick-100 rounded-sm border bg-white p-2 text-xs"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="text-brick-700 font-medium">
                                {doc.type} - {doc.documentNumber}
                              </div>
                              <div className="text-neutral-600">
                                {doc.supplier} • {doc.partTitle}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-brick-600 font-medium">
                                {formatCurrency(doc.totalAmount)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="text-brick-600 mt-3 text-xs">
                      <strong>Let op:</strong> Facturen blijven beschikbaar in
                      Odoo en kunnen opnieuw geïmporteerd worden.
                    </div>
                  </div>
                )}

                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <p className="text-sm text-red-700">
                    <strong>
                      ⚠️ Deze actie kan niet ongedaan worden gemaakt
                    </strong>
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <NextUISpinner size="md" />
                <span className="ml-3 text-neutral-600">
                  Project informatie laden...
                </span>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="light"
              onPress={() => setShowDeleteModal(false)}
              isDisabled={isDeleting}
            >
              Annuleren
            </Button>
            <Button
              color="danger"
              onPress={handleConfirmDelete}
              isLoading={isDeleting}
              isDisabled={!deletionInfo || isDeleting}
              className="bg-brick-primary hover:bg-brick-primary/90 text-white"
            >
              {isDeleting ? "Verwijderen..." : "Project verwijderen"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Responsible user selection */}
      <Modal
        isOpen={isResponsibleModalOpen}
        onClose={() => setIsResponsibleModalOpen(false)}
      >
        <ModalContent
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <ModalHeader>
            <h3 className="text-lg font-medium">Verantwoordelijke toewijzen</h3>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-3">
              <div className="text-sm text-neutral-600">
                Kies een gebruiker als verantwoordelijke voor dit project.
              </div>
              {/* Snelle selectie, zoals in QuickAddTicketModal */}
              {activeUsers && activeUsers.length > 0 ? (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                  {activeUsers.map((u: any) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => handleResponsibleSelect(u)}
                      className={`flex items-center gap-2 rounded-md border p-2 text-left transition ${
                        responsibleId === u.id
                          ? "border-moss-primary bg-moss-primary/10"
                          : "border-neutral-primary/20 hover:border-moss-primary"
                      }`}
                    >
                      <img
                        src={u.image_url}
                        alt={`${u.first_name} ${u.last_name}`}
                        className="h-7 w-7 rounded-full object-cover"
                        onError={(e: any) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                      {!u.image_url && (
                        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-neutral-200 text-xs text-neutral-600">
                          ?
                        </div>
                      )}
                      <span className="text-neutral-primary truncate text-sm">
                        {u.first_name} {u.last_name}
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-neutral-500">
                  Geen gebruikers gevonden.
                </div>
              )}

              {/* Alternatieve selector (zoek) */}
              <div className="border-t border-neutral-100 pt-2">
                <UserSelection
                  defaultUser={responsibleUser || null}
                  onUserSelect={(u: any) => handleResponsibleSelect(u)}
                />
              </div>

              <div>
                <Button
                  variant="bordered"
                  className="text-neutral-600"
                  onPress={() => handleResponsibleSelect(null as any)}
                >
                  Geen verantwoordelijke
                </Button>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="light"
              onPress={() => setIsResponsibleModalOpen(false)}
            >
              Sluiten
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Pictures Modal */}
      <Modal
        isOpen={showPicturesModal}
        onClose={() => setShowPicturesModal(false)}
        size="5xl"
        scrollBehavior="inside"
      >
        <ModalContent
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <ModalHeader className="flex flex-col gap-1">
            <div className="flex items-center gap-3">
              <Camera size={24} className="text-moss-primary" />
              <div>
                <h3 className="text-lg font-medium">Project Foto's</h3>
                <p className="text-sm font-normal text-neutral-500">
                  RENO-{id} - {title}
                </p>
              </div>
            </div>
          </ModalHeader>
          <ModalBody>
            <Tabs
              aria-label="Project foto categorieën"
              color="primary"
              variant="underlined"
              classNames={{
                tabList:
                  "gap-6 w-full relative rounded-none p-0 border-b border-divider",
                cursor: "w-full bg-moss-primary",
                tab: "max-w-fit px-0 h-12",
                tabContent: "group-data-[selected=true]:text-moss-primary",
              }}
            >
              <Tab
                key="voorafgaand"
                title={
                  <div className="flex items-center space-x-2">
                    <span>📋</span>
                    <span>
                      Voorafgaand ({dummyPictures.voorafgaand.length})
                    </span>
                  </div>
                }
              >
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {dummyPictures.voorafgaand.map((pic) => (
                    <Card
                      key={pic.id}
                      className="group transition-shadow hover:shadow-lg"
                    >
                      <CardBody className="p-0">
                        <img
                          src={pic.url}
                          alt={pic.caption}
                          className="h-48 w-full rounded-t-lg object-cover"
                        />
                        <div className="p-3">
                          <p className="text-sm text-neutral-600">
                            {pic.caption}
                          </p>
                        </div>
                      </CardBody>
                    </Card>
                  ))}
                </div>
              </Tab>
              <Tab
                key="werfopvolging"
                title={
                  <div className="flex items-center space-x-2">
                    <span>🚧</span>
                    <span>
                      Werfopvolging ({dummyPictures.werfopvolging.length})
                    </span>
                  </div>
                }
              >
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {dummyPictures.werfopvolging.map((pic) => (
                    <Card
                      key={pic.id}
                      className="group transition-shadow hover:shadow-lg"
                    >
                      <CardBody className="p-0">
                        <img
                          src={pic.url}
                          alt={pic.caption}
                          className="h-48 w-full rounded-t-lg object-cover"
                        />
                        <div className="p-3">
                          <p className="text-sm text-neutral-600">
                            {pic.caption}
                          </p>
                        </div>
                      </CardBody>
                    </Card>
                  ))}
                </div>
              </Tab>
              <Tab
                key="eindresultaat"
                title={
                  <div className="flex items-center space-x-2">
                    <span>✅</span>
                    <span>
                      Eindresultaat ({dummyPictures.eindresultaat.length})
                    </span>
                  </div>
                }
              >
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {dummyPictures.eindresultaat.map((pic) => (
                    <Card
                      key={pic.id}
                      className="group transition-shadow hover:shadow-lg"
                    >
                      <CardBody className="p-0">
                        <img
                          src={pic.url}
                          alt={pic.caption}
                          className="h-48 w-full rounded-t-lg object-cover"
                        />
                        <div className="p-3">
                          <p className="text-sm text-neutral-600">
                            {pic.caption}
                          </p>
                        </div>
                      </CardBody>
                    </Card>
                  ))}
                </div>
              </Tab>
            </Tabs>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setShowPicturesModal(false)}>
              Sluiten
            </Button>
            <Button color="primary" className="bg-moss-primary">
              <Camera size={16} />
              Upload Foto's
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* RFP (Request for Proposal) Modal */}
      <Modal
        isOpen={showRFPModal}
        onClose={() => setShowRFPModal(false)}
        size="3xl"
      >
        <ModalContent
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <ModalHeader className="flex flex-col gap-1">
            <div className="flex items-center gap-3">
              <FileArrowDown size={24} className="text-brick-primary" />
              <div>
                <h3 className="text-lg font-medium">Offerte Aanvragen</h3>
                <p className="text-sm font-normal text-neutral-500">
                  RENO-{id} - {title}
                </p>
              </div>
            </div>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-6">
              <div>
                <label className="mb-2 block text-sm font-medium text-neutral-700">
                  Selecteer Projectonderdeel
                </label>
                <div className="grid gap-2">
                  {parts.map((part) => (
                    <button
                      key={part.id}
                      onClick={() => setSelectedRFPPart(part)}
                      className={`rounded-lg border p-3 text-left transition-colors ${
                        selectedRFPPart?.id === part.id
                          ? "border-moss-primary bg-moss-primary/10"
                          : "hover:border-moss-primary/50 border-neutral-200"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{part.title}</p>
                          <p className="text-sm text-neutral-500">
                            RENO-{id}-O{part.id}
                          </p>
                          {part.label && (
                            <span className="bg-moss-primary/10 text-moss-primary mt-1 inline-block rounded-full px-2 py-0.5 text-xs">
                              {part.label.name}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-neutral-500">
                          {part.supplierAssignments?.length || 0} leverancier
                          {part.supplierAssignments?.length !== 1 ? "s" : ""}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {selectedRFPPart && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-neutral-700">
                    Selecteer Leverancier
                  </label>
                  {selectedRFPPart.supplierAssignments?.length > 0 ? (
                    <div className="grid gap-2">
                      {selectedRFPPart.supplierAssignments.map(
                        (assignment: any) => (
                          <button
                            key={assignment.id}
                            onClick={() =>
                              setSelectedRFPSupplier(assignment.supplier)
                            }
                            className={`rounded-lg border p-3 text-left transition-colors ${
                              selectedRFPSupplier?.id === assignment.supplier.id
                                ? "border-brick-primary bg-brick-primary/10"
                                : "hover:border-brick-primary/50 border-neutral-200"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="bg-brick-primary/20 flex h-8 w-8 items-center justify-center rounded-full">
                                <User
                                  size={16}
                                  className="text-brick-primary"
                                />
                              </div>
                              <div>
                                <p className="font-medium">
                                  {assignment.supplier.name}
                                </p>
                                <p className="text-sm text-neutral-500">
                                  Status:{" "}
                                  {assignment.status || "Niet verzonden"}
                                </p>
                              </div>
                            </div>
                          </button>
                        ),
                      )}
                    </div>
                  ) : (
                    <div className="rounded-lg bg-neutral-50 p-4 text-center">
                      <p className="text-neutral-500">
                        Geen leveranciers toegewezen aan dit onderdeel
                      </p>
                    </div>
                  )}
                </div>
              )}

              {selectedRFPPart && selectedRFPSupplier && (
                <div className="bg-moss-primary/5 border-moss-primary/20 rounded-lg border p-4">
                  <h4 className="text-moss-primary mb-2 font-medium">
                    Offerte Details
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <strong>Project:</strong> {title}
                    </div>
                    <div>
                      <strong>Onderdeel:</strong> {selectedRFPPart.title}
                    </div>
                    <div>
                      <strong>Leverancier:</strong> {selectedRFPSupplier.name}
                    </div>
                    <div>
                      <strong>Categorie:</strong>{" "}
                      {selectedRFPPart.label?.name || "Geen categorie"}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="light"
              onPress={() => {
                setShowRFPModal(false);
                setSelectedRFPPart(null);
                setSelectedRFPSupplier(null);
              }}
            >
              Annuleren
            </Button>
            <Button
              color="primary"
              className="bg-brick-primary"
              isDisabled={!selectedRFPPart || !selectedRFPSupplier}
              startContent={<Envelope size={16} />}
            >
              Verstuur Offerte Aanvraag
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Inline categorie / label picker per project part — opens from any
          part card's label chip. Reuses the universal picker so the look &
          shortcut behaviour matches the rest of the app. */}
      {partLabelPicker && (
        <UniversalPickerModal
          isOpen={true}
          mode="custom-tags"
          title="Selecteer categorie"
          onClose={() => setPartLabelPicker(null)}
          onConfirm={(val) => handlePartLabelChange(val as string | null)}
          config={{
            defaultValue:
              partLabelPicker.currentId != null
                ? String(partLabelPicker.currentId)
                : null,
            allowRemoval: true,
            customOptions: renovationLabels.map((l) => ({
              value: String(l.id),
              label: l.type ? `${l.name} (${l.type})` : l.name,
            })),
          }}
        />
      )}
    </div>
  );
};

export default ProjectCard;
